import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Chip, Dialog,
    DialogContent,
    DialogTitle, Paper,
    TextField
} from "@mui/material";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { PAContext } from "../../../Services/PAContext";
import ConciergeMessageCard from "./ConciergeMessageCard";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import './style.css';

// --- 1. CHAT INPUT (MOVED OUTSIDE TO FIX KEYBOARD FOCUS BUG) ---
const ChatInput = memo(({ onSendMessage, geminiToolCalls, disabled }) => {
    const [localMessage, setLocalMessage] = useState("");
    const inputRef = useRef(null);

    const handleInternalSend = () => {
        if (localMessage.trim() && !disabled) {
            onSendMessage(localMessage.trim());
            setLocalMessage("");
        }
    };

    return (
        <div className="concierge-messages-list-items-footer-container">
            <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
                variant="outlined"
                label="Type your message"
                value={localMessage}
                onChange={(e) => setLocalMessage(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleInternalSend();
                        e.currentTarget.blur();
                    }
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: disabled ? '#f5f5f5' : '#fff' } }}
            />
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px', width: '100%' }}>
                {geminiToolCalls && (
                    <div style={{ fontSize: '10px', color: '#999', fontStyle: 'italic', padding: '5px 15px' }}>
                        Fritz is using: <strong>{geminiToolCalls.replace(/_/g, ' ')}</strong>...
                    </div>
                )}
                <Chip
                    label={disabled ? "Processing..." : "Send"}
                    disabled={disabled} // 🎯 Disable the chip
                    onClick={(e) => {
                        handleInternalSend();
                        e.currentTarget.blur(); // 🎯 Crucial: prevents the "sticky grey" focus
                    }}
                    sx={{
                        marginLeft: 'auto',
                        backgroundColor: '#151269',
                        color: '#fff',
                        // Ensure it looks right when disabled
                        '&.Mui-disabled': {
                            backgroundColor: '#ccc',
                            color: '#888'
                        }
                    }}
                />
            </div>
        </div>
    );
});




function ConciergeService(props) {
    const { PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, geminiMessages, setGeminiMessages, geminiToolCalls, sendSocketMessage } = useContext(PAContext);
    const { Account, authenticated } = dataArray;

    const [isSyncing, setIsSyncing] = useState(false);
    const [energyUsage, setEnergyUsage] = useState({ totalTokens: 0, cost: 0 });
    const [usageLogs, setUsageLogs] = useState([]);
    const [usageDialogOpen, setUsageDialogOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

    const messagesContainerRef = useRef(null);

    useEffect(() => {
        // When the screen loads, if the last message is a "model" message
        // that was left in a non-final state, mark it final so the user can type.
        setGeminiMessages(prev => {
            if (prev.length === 0) return prev;
            const last = prev[prev.length - 1];
            if (last.role === "model" && !last.isFinal) {
                const updated = [...prev];
                updated[updated.length - 1] = { ...last, isFinal: true };
                return updated;
            }
            return prev;
        });
    }, []); // 🎯 Runs ONLY once on mount

    // Fetch Usage Logs
    useEffect(() => {
        if (!Account?.id) return;

        const fetchUsage = async () => {
            setIsSyncing(true);
            try {
                const response = await fetch(`https://pacloudrun-1070636036545.us-east1.run.app/api/concierge/usage/${Account.id}`);
                const data = await response.json();
                setUsageLogs(data);

                const tokens = data.reduce((sum, entry) => sum + (entry.total_tokens || 0), 0);
                // Updated Pricing logic: $0.30/1M prompt, $2.50/1M response
                const cost = data.reduce((sum, entry) => {
                    return sum + ((entry.prompt_tokens * 0.0000003) + (entry.response_tokens * 0.0000025));
                }, 0);

                setEnergyUsage({ totalTokens: tokens, cost: cost });
            } catch (err) {
                console.error("Failed to fetch usage", err);
            } finally {
                setTimeout(() => setIsSyncing(false), 500);
            }

            if (geminiMessages.length > 0) {
                const last = geminiMessages[geminiMessages.length - 1];
                if (last.role === "model" && last.isFinal) {
                    // This ensures the memo re-evaluates and turns the Chip back on
                }
            }
        };

        fetchUsage();
    }, [Account.id, geminiMessages.length]);

    // Handle Auto-Scroll
    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [geminiMessages]);

    const isWaitingForFritz = useMemo(() => {
        if (geminiMessages.length === 0) return false;

        const lastMsg = geminiMessages[geminiMessages.length - 1];

        // If the last message is from the user, we are definitely waiting.
        if (lastMsg.role === "user") return true;

        // If the last message is from the model but NOT final, we are waiting...
        if (lastMsg.role === "model") {
            // 🎯 SAFETY: If there are no active tool calls, Fritz might be stuck.
            // You could add a 'isStreaming' state from your Context here.
            return true;
        }

        return false;
    }, [geminiMessages, geminiToolCalls]); // 🎯 Add geminiToolCalls as a dependency

    // Derived Data: Monthly Stats
    const filteredLogs = useMemo(() => {
        return usageLogs.filter(log => new Date(log.timestamp).toISOString().slice(0, 7) === selectedMonth);
    }, [usageLogs, selectedMonth]);

    const monthlyTotal = useMemo(() => {
        return filteredLogs.reduce((sum, log) => {
            return sum + (log.prompt_tokens * 0.0000003) + (log.response_tokens * 0.0000025);
        }, 0);
    }, [filteredLogs]);

    const availableMonths = useMemo(() => {
        const months = [...new Set(usageLogs.map(log => new Date(log.timestamp).toISOString().slice(0, 7)))];
        return months.sort().reverse();
    }, [usageLogs]);

    const handleSendMessage = (textFromInput) => {
        const messageObj = {
            attendee_id: Account.id,
            created: Date.now(),
            message: textFromInput,
            role: "user"
        };

        setGeminiMessages(prev => [...prev, messageObj]);

        if (PA_GlobalObject.Planning.status!== 10) {
            sendSocketMessage({
                action: "CONCIERGE_PROMPT",
                event_id: PA_GlobalObject.Planning.id,
                currentRoute: window.location.pathname,
                message: textFromInput
            });
        }
    };

    const renderedMessages = useMemo(() => {
        if (!geminiMessages || geminiMessages.length === 0) {
            return <div style={{ textAlign: 'center', color: '#999', marginTop: '20px' }}><p>No Messages Yet</p></div>;
        }

        return [...geminiMessages]
            .sort((a, b) => a.created - b.created)
            .map((_message, index) => (
                <ConciergeMessageCard
                    key={_message.id || index}
                    message={_message}
                    align={_message.attendee_id === Account.id ? "right" : "left"}
                    setCurrentFeature={props.setCurrentFeature}
                    showAccountDialog={props.showAccountDialog}
                    setShowAccountDialog={props.setShowAccountDialog}
                    eventPointsDialogOpen={props.eventPointsDialogOpen}
                    setEventPointsDialogOpen={props.setEventPointsDialogOpen}
                />
            ));
    }, [geminiMessages, Account.id, props]);



    return (
        <>
            <div className="concierge-messages-list-container">
                <div className="concierge-messages-list-header">
                    <div className="messages-list-header-text">
                        <h5>Gemini Concierge</h5>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingRight: '10px' }}>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', fontWeight: 'bold' }}>Concierge Energy</div>
                            <div style={{ fontSize: '11px', fontWeight: 'mono', color: '#151269' }}>${energyUsage.cost.toFixed(4)}</div>
                        </div>
                        <div onClick={() => setUsageDialogOpen(true)}
                             style={{
                                 width: '40px',
                                 height: '18px',
                                 border: isSyncing ? '1.5px solid #4caf50' : '1px solid #555',
                                 boxShadow: isSyncing ? '0 0 8px #4caf50' : 'none',
                                 borderRadius: '3px',
                                 padding: '2px',
                                 position: 'relative',
                                 cursor: 'pointer',
                                 transition: 'all 0.3s ease'
                             }}>
                            <div style={{
                                position: 'absolute',
                                right: '-4px',
                                top: '7px',
                                width: '3px',
                                height: '8px',
                                backgroundColor: isSyncing ? '#4caf50' : '#555',
                                borderRadius: '0 2px 2px 0'
                            }}/>
                            <div style={{
                                // (Current Cost / $20 Budget) * 100
                                width: `${Math.min((energyUsage.cost / 20) * 100, 100)}%`,
                                height: '100%',
                                // Change to Orange at $15, Red at $18
                                backgroundColor: energyUsage.cost > 18 ? '#f44336' : energyUsage.cost > 15 ? '#ff9800' : '#4caf50',
                                transition: 'width 0.5s ease'
                            }}/>
                        </div>
                    </div>
                </div>


                <div className="concierge-messages-list-items-container" ref={messagesContainerRef}>
                    {renderedMessages}
                </div>

                <ChatInput
                    onSendMessage={handleSendMessage}
                    geminiToolCalls={geminiToolCalls}
                    disabled={false}
                    //disabled={isWaitingForFritz}
                />
            </div>

            <Dialog open={usageDialogOpen} onClose={() => setUsageDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: '12px' } }}>
                <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                    <Typography style={{ fontWeight: 800 }}>POWER AUDIT</Typography>
                    <IconButton onClick={() => setUsageDialogOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 24px', backgroundColor: '#fcfdfe' }}>
                    <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={{ padding: '4px', borderRadius: '4px' }}>
                        {availableMonths.map(m => <option key={m} value={m}>{dayjs(m).format('MMMM YYYY')}</option>)}
                    </select>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: '#90a4ae' }}>MONTHLY INVESTMENT</div>
                        <div style={{ fontSize: '18px', fontWeight: '900', color: '#2e7d32' }}>${monthlyTotal.toFixed(4)}</div>
                    </div>
                </div>
                <DialogContent style={{ backgroundColor: '#f8f9fa' }}>
                    {filteredLogs.map((entry, idx) => (
                        <Paper key={idx} elevation={0} style={{ padding: '12px', marginBottom: '12px', border: '1px solid #e0e4e8', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '8px' }}>
                                <span>P: {entry.prompt_tokens?.toLocaleString()} (${(entry.prompt_tokens * 0.0000003).toFixed(5)})</span>
                                <span>R: {entry.response_tokens?.toLocaleString()} (${(entry.response_tokens * 0.0000025).toFixed(5)})</span>
                            </div>
                            <Typography variant="caption" color="textSecondary">{entry.prompt}</Typography>
                            {entry.response && (
                                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f1f8e9', borderRadius: '4px', fontSize: '12px', fontStyle: 'italic' }}>
                                    {entry.response.replace(/<[^>]*>?/gm, '')}
                                </div>
                            )}
                        </Paper>
                    ))}
                </DialogContent>
            </Dialog>
        </>
    );
}

export default ConciergeService;